import { useEffect, useRef } from 'react';

import { Box, Button, Heading, Stack, useColorModeValue } from '@ecoinvent/ui';

import { TourContextValue, TourStep } from '../types';

import StepIndicator from './StepIndicator';

interface CompletionCardProps {
  steps: TourStep[];
  useTourHook: () => TourContextValue;
}

const CompletionCard = ({ steps, useTourHook }: CompletionCardProps) => {
  const { state, prevStep, endTour } = useTourHook();
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.200');

  const completionStepIndex = steps.findIndex((s) => s.id === 'completion');
  const step = steps[completionStepIndex];

  const isOpen = state.isActive && state.currentStep === completionStepIndex && step?.type === 'card';

  const coachmarkSteps = steps.filter((s) => s.type === 'coachmark');

  const cardRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const headingId = 'completion-card-heading';
  const contentId = 'completion-card-content';

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      cardRef.current?.focus();
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  if (!isOpen || !step) {
    return null;
  }

  return (
    <Box
      ref={cardRef}
      role="dialog"
      aria-labelledby={headingId}
      aria-describedby={contentId}
      aria-modal="true"
      tabIndex={-1}
      position="fixed"
      right="24px"
      bottom="24px"
      zIndex="popover"
      p={6}
      bg={bgColor}
      borderRadius="lg"
      maxW="320px"
      boxShadow="xl"
      borderWidth="0.5px"
      borderColor={borderColor}
      _focus={{ outline: 'none' }}
    >
      <Stack spacing={4}>
        <Heading id={headingId} size="md">
          {step.title}
        </Heading>
        <Box id={contentId} fontSize="sm">
          {step.content}
        </Box>

        <Stack direction="row" spacing={2} justify="space-between" align="center">
          <Button variant="outline" colorScheme="gray" size="sm" onClick={prevStep}>
            Back
          </Button>
          <StepIndicator totalSteps={coachmarkSteps.length} currentStep={coachmarkSteps.length} />
          <Button variant="solid" size="sm" onClick={endTour}>
            {step.buttonText}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CompletionCard;
